import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../../contexts/CartProvider";
import { useAuth } from "../../contexts/AuthProvider";
import { http } from "../../services/http";
import { useTranslation } from "react-i18next";
import {
  ShoppingBag,
  Menu as MenuIcon,
  LogIn,
  LogOut,
  ChevronRight,
  User,
  FileText,
  ChevronDown,
  Star,
} from "lucide-react";

/* ======================
   HELPERS
====================== */
function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function useIsScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

function useClickOutside(
  refs: Array<React.RefObject<HTMLElement>>,
  onOutside: () => void,
) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      const inside = refs.some((r) => r.current && r.current.contains(t));
      if (!inside) onOutside();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [refs, onOutside]);
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/* ======================
   TYPES for Profile
====================== */
type ProfileApi = {
  availablePoints?: number;
  pointValue?: number;
  pointToMoneyRate?: number;
  user?: {
    username?: string;
    email?: string;
    pointAccumulated?: number;
    pointUsed?: number;
  };
};

const PROFILE_ENDPOINT = "/api/profile"; // ⬅️ đổi thành endpoint profile thật của bạn

export default function Header() {
  const { t, i18n } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const [profile, setProfile] = useState<ProfileApi | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const accountBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { toggle: toggleCart, count, isOpen: isCartOpen } = useCart();
  const { user, logout } = useAuth();

  const scrolled = useIsScrolled(10);

  const closeMenu = () => setMenuOpen(false);
  const closeAccount = () => setAccountOpen(false);

  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const moneyLocale = lang === "en" ? "en-US" : "vi-VN";

  const toggleLanguage = () => {
    i18n.changeLanguage(lang === "vi" ? "en" : "vi");
  };

  // Close when clicking outside (works with Portal too)
  useClickOutside(
    [dropdownRef, { current: accountBtnRef.current as any }],
    () => setAccountOpen(false),
  );

  const handleLogout = () => {
    if (isCartOpen) toggleCart();
    logout();
    setAccountOpen(false);
    closeMenu();
    navigate("/auth", { replace: true });
  };

  const handleOpenCart = () => {
    if (menuOpen) closeMenu();
    setAccountOpen(false);
    toggleCart();
  };

  useEffect(() => {
    closeMenu();
    closeAccount();
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      document.body.classList.remove("modal-open");
      return;
    }
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMenu();
        closeAccount();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // ✅ Fetch profile points for dropdown (from profile API)
  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      setProfileLoading(true);
      try {
        const res = await http.get(PROFILE_ENDPOINT);
        if (!mounted) return;
        setProfile(res.data);
      } catch {
        // nếu endpoint khác hoặc lỗi thì bỏ qua, vẫn render dropdown bình thường
        if (!mounted) return;
        setProfile(null);
      } finally {
        mounted && setProfileLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [user]);

  const navItems = useMemo(
    () => [
      { to: "/collection", labelKey: "nav.shop" },
      { to: "/drops", labelKey: "nav.drops" },
      // { to: "/lookbook", labelKey: "nav.lookbook" },
      { to: "/voucher-game", labelKey: "nav.voucherGame" },
      { to: "/policies", labelKey: "nav.policies" },
    ],
    [],
  );

  const desktopNavClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "relative inline-flex items-center",
      "text-[11px] uppercase tracking-[0.22em]",
      "transition-colors",
      isActive
        ? "text-brand-gold"
        : "text-brand-ivory/80 hover:text-brand-gold",
    );

  // ✅ points from profile (ưu tiên availablePoints)
  const points =
    profile?.availablePoints ??
    profile?.user?.pointAccumulated ??
    (user as any)?.availablePoints ??
    (user as any)?.points ??
    null;

  const pointToMoneyRate = profile?.pointToMoneyRate ?? 1000;
  const pointValue =
    profile?.pointValue ?? (points != null ? points * pointToMoneyRate : null);

  /* ======================
     DROPDOWN POSITION (fixed + portal)
  ======================= */
  const [dropPos, setDropPos] = useState<{
    top: number;
    left: number;
    width: number;
  }>({
    top: 0,
    left: 0,
    width: 288,
  });

  const updateDropdownPos = () => {
    const btn = accountBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const width = 292; // đẹp hơn 256, không quá to
    const gap = 10;

    const top = r.bottom + gap;
    // căn theo right của button nhưng clamp trong viewport
    const left = clamp(r.right - width, 10, window.innerWidth - width - 10);

    setDropPos({ top, left, width });
  };

  useEffect(() => {
    if (!accountOpen) return;
    updateDropdownPos();

    const onResize = () => updateDropdownPos();
    const onScroll = () => updateDropdownPos();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, [accountOpen]);

  // ✅ Toggle dropdown: compute pos before open to avoid jump
  const toggleAccount = () => {
    if (!accountOpen) updateDropdownPos();
    setAccountOpen((v) => !v);
  };

  const accountDropdown = accountOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 9999,
          }}
          className={cn(
            "rounded-2xl border border-black/10 bg-white/95 backdrop-blur",
            "shadow-[0_18px_55px_-25px_rgba(0,0,0,0.55)] overflow-hidden",
            "animate-[fadeIn_.14s_ease_both]",
          )}
          role="menu"
          aria-label={t("header.accountMenuAria")}
        >
          <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

          {/* header */}
          <div className="px-4 py-3 bg-black/[0.03]">
            <p className="text-sm font-semibold text-neutral-900 truncate">
              {(profile?.user?.username as any) ||
                (user as any)?.username ||
                t("header.user")}
            </p>
            <p className="text-xs text-neutral-600 truncate">
              {(profile?.user?.email as any) || (user as any)?.email || "—"}
            </p>
          </div>

          {/* points */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-neutral-700">
                <Star className="w-4 h-4" />
                <span className="text-xs">{t("header.availablePoints")}</span>
              </div>
              <span className="text-sm font-semibold text-neutral-900">
                {profileLoading ? "…" : (points ?? "—")}
              </span>
            </div>

            <div className="mt-1 text-[11px] text-neutral-500">
              {points != null ? (
                <>
                  ~ {Number(pointValue ?? 0).toLocaleString(moneyLocale)}₫ •{" "}
                  {t("header.pointRule", {
                    rate: Number(pointToMoneyRate).toLocaleString(moneyLocale),
                  })}
                </>
              ) : (
                "—"
              )}
            </div>
          </div>

          <div className="h-px bg-black/5" />

          {/* actions */}
          <div className="p-2">
            <Link
              to="/profile"
              onClick={() => setAccountOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-neutral-800 hover:bg-black/[0.04] transition"
              role="menuitem"
            >
              <User className="w-4 h-4" />
              {t("header.myProfile")}
            </Link>
            <Link
              to="/voucher-game"
              onClick={() => setAccountOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-neutral-800 hover:bg-black/[0.04] transition"
              role="menuitem"
            >
              <Star className="w-4 h-4" />
              {t("nav.voucherGame")}
            </Link>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-neutral-800 hover:bg-black/[0.04] transition"
              role="menuitem"
            >
              <LogOut className="w-4 h-4" />
              {t("header.logout")}
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      {/* ================= HEADER ================= */}
      <header className="fixed top-0 left-0 w-full z-50">
        <div
          className={cn(
            "absolute inset-0 transition-all",
            scrolled
              ? "bg-brand-brown/85 backdrop-blur-md"
              : "bg-brand-brown/60 backdrop-blur-sm",
          )}
        />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-brand-ivory/10" />

        {/* ✅ compact + airy hơn */}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between text-brand-ivory">
          {/* Logo */}
          <Link
            to="/"
            className="
    flex items-center gap-3 shrink-0
    px-2 py-1 rounded-full
    hover:bg-brand-ivory/5 transition
  "
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo/owl-icon.png`}
              alt="OWL DREAM"
              className="h-9 sm:h-10 w-auto object-contain"
            />

            <span className="font-brand-logo text-[11px] sm:text-xs text-brand-ivory">
              OWL&nbsp;DREAM
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} className={desktopNavClass}>
                {t(it.labelKey)}
              </NavLink>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language switch (desktop) */}
            <button
              onClick={toggleLanguage}
              className="hidden sm:inline-flex items-center h-9 px-3 rounded-full border border-brand-ivory/15 bg-brand-ivory/5 hover:border-brand-gold/60 hover:text-brand-gold transition text-[11px] uppercase tracking-[0.20em]"
              aria-label={t("header.switchLanguageAria")}
              title={t("header.switchLanguageTitle")}
              type="button"
            >
              {lang === "vi" ? "EN" : "VI"}
            </button>

            {/* ✅ My account dropdown (desktop) */}
            {user ? (
              <div className="hidden sm:block">
                <button
                  ref={accountBtnRef}
                  onClick={toggleAccount}
                  className={cn(
                    "inline-flex items-center gap-2",
                    "h-9 px-3 rounded-full",
                    "border border-brand-ivory/15 bg-brand-ivory/5",
                    "hover:border-brand-gold/60 hover:text-brand-gold transition",
                  )}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  type="button"
                >
                  <User className="w-4 h-4" />
                  <span className="text-[11px] uppercase tracking-[0.20em]">
                    {t("header.myAccount")}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-80" />
                </button>

                {/* portal dropdown */}
                {accountDropdown}
              </div>
            ) : (
              <Link
                to="/auth"
                className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-brand-ivory/15 bg-brand-ivory/5 hover:border-brand-gold/60 hover:text-brand-gold transition"
              >
                <LogIn className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-[0.20em]">
                  {t("header.login")}
                </span>
              </Link>
            )}

            {/* Cart (compact) */}
            <button
              onClick={handleOpenCart}
              className="relative inline-flex items-center gap-2 h-9 px-3 rounded-full border border-brand-ivory/15 hover:border-brand-gold/60 hover:text-brand-gold transition"
              type="button"
              aria-label={t("header.cart")}
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline text-[11px] uppercase tracking-[0.20em]">
                {t("header.cart")}
              </span>
              <span className="ml-1 min-w-[20px] h-[18px] px-2 inline-flex items-center justify-center text-[10px] rounded-full bg-brand-gold text-brand-brown">
                {count}
              </span>
            </button>

            {/* Mobile menu */}
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden h-9 w-9 rounded-full border border-brand-ivory/15 hover:border-brand-gold/60 hover:text-brand-gold transition"
              aria-label={t("header.openMenuAria")}
              type="button"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ================= MOBILE DRAWER ================= */}
      <div
        className={cn(
          "fixed inset-0 z-[55] transition-opacity",
          menuOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
      >
        <div
          className="absolute inset-0 bg-brand-brown/70"
          onClick={closeMenu}
        />

        <div
          className={cn(
            "absolute top-0 right-0 h-full w-[86%] max-w-sm bg-brand-ivory text-brand-brown transition-transform",
            menuOpen ? "translate-x-0" : "translate-x-full",
          )}
        >
          <div className="p-6 space-y-6">
            {/* Language switch (mobile) */}
            <button
              onClick={toggleLanguage}
              className="flex items-center justify-between h-11 px-4 border rounded-xl uppercase tracking-[0.28em] text-xs border-brand-brown/15 hover:border-brand-brown transition"
              type="button"
            >
              <span>{t("header.language")}</span>
              <span className="font-semibold">
                {lang === "vi" ? "EN" : "VI"}
              </span>
            </button>

            {/* Nav */}
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) =>
                  cn(
                    "flex justify-between items-center h-11 px-4 border rounded-xl transition",
                    isActive
                      ? "border-brand-brown bg-brand-brown text-brand-ivory"
                      : "border-brand-brown/15 hover:border-brand-brown",
                  )
                }
              >
                <span className="uppercase tracking-[0.28em] text-xs">
                  {t(it.labelKey)}
                </span>

                {it.to === "/policies" ? (
                  <FileText className="w-4 h-4 opacity-80" />
                ) : it.to === "/voucher-game" ? (
                  <Star className="w-4 h-4 opacity-80" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </NavLink>
            ))}

            <div className="h-px bg-brand-brown/10" />

            {/* Auth mobile */}
            {user ? (
              <>
                <Link
                  to="/profile"
                  className="flex items-center gap-3 h-11 px-4 border rounded-xl uppercase tracking-[0.28em] text-xs border-brand-brown/15 hover:border-brand-brown transition"
                >
                  <User className="w-4 h-4" />
                  {t("header.myAccount")}
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 h-11 px-4 border rounded-xl uppercase tracking-[0.28em] text-xs border-brand-brown/15 hover:border-brand-brown transition"
                  type="button"
                >
                  <LogOut className="w-4 h-4" />
                  {t("header.logout")}
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                className="flex items-center gap-3 h-11 px-4 border rounded-xl uppercase tracking-[0.28em] text-xs border-brand-brown/15 hover:border-brand-brown transition"
              >
                <LogIn className="w-4 h-4" />
                {t("header.login")}
              </Link>
            )}

            <button
              onClick={handleOpenCart}
              className="flex items-center justify-between h-11 px-4 bg-brand-brown text-brand-ivory rounded-xl uppercase tracking-[0.28em] text-xs"
              type="button"
            >
              <span className="flex items-center gap-3">
                <ShoppingBag className="w-4 h-4" />
                {t("header.cart")}
              </span>
              {count > 0 && <span>({count})</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-14 sm:h-16" />
    </>
  );
}
